import React, { useState, useEffect, useMemo, useRef, useCallback, } from 'react';
import { useTranslation } from 'react-i18next';
import { getTokens } from '../../../utils/handleTokens';
import { v4 as uuidv4 } from 'uuid';
// Import API hook and types
import { useReadDocumentsV2, useReadDocumentTypes, useReadDocument, } from '../../../services/online/repository/repository';
import { SortBy, DocumentRelation, } from '../../../services/types';
import { useCompaniesContext } from '../../../contexts/companies';
// Import Chainlit hooks
import { useChatSession, useChatMessages, useChatInteract, useChatData, } from '@chainlit/react-client';
import { keyframes, usePrefersReducedMotion } from '@chakra-ui/react';
// Dummy getTypeColor function (replace with actual logic if needed)
const getTypeColor = (typeName) => {
    // Simplified version based on DocumentListTableRow
    // Use your actual color constants
    switch (typeName) {
        case 'Meeting Minutes':
            return 'blue.500';
        case 'Financial Report':
            return 'green.500';
        case 'Strategic Plan':
            return 'purple.500';
        default:
            return 'gray.500';
    }
};
export const useAIAssistLogic = () => {
    // Attributes
    const { t } = useTranslation();
    const { selectedCompanyId, selectedEnrolment } = useCompaniesContext(); // Get selectedEnrolment
    const enrolmentId = selectedEnrolment?.id; // Derive enrolmentId
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false); // New state for new chat confirmation
    const [selectedDocuments, setSelectedDocuments] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [currentMessage, setCurrentMessage] = useState('');
    const [retrievedAccessToken, setRetrievedAccessToken] = useState(null); // State for the access token
    const [documentPreviewInitialPage, setDocumentPreviewInitialPage] = useState(null); // State for target page
    // Ref for scrolling the message list
    const messagesEndRef = React.useRef(null);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    // Animation keyframes
    const fadeIn = keyframes `
    from { opacity: 0; }
    to { opacity: 1; }
  `;
    const slideIn = keyframes `
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  `;
    const animation = prefersReducedMotion
        ? undefined
        : `${fadeIn} 0.3s ease-in, ${slideIn} 0.4s ease-out`;
    // Chainlit State
    const { connect, disconnect, session } = useChatSession(); // Get session
    const chainlitConnected = session?.socket.connected ?? false; // Derive connected status
    const { messages: chainlitMessages } = useChatMessages(); // Renamed to avoid conflict
    const { sendMessage, clear: clearMessages } = useChatInteract();
    const { loading: chatLoading, error: chatError } = useChatData(); // Removed connected from here as it's from useChatSession
    console.log('Chainlit Messages Updated:', chainlitMessages); // DEBUG LOG
    // Document Viewer State - Holds ID now
    const [viewingDocumentId, setViewingDocumentId] = useState(null);
    // Filter State
    const [filterDateFrom, setFilterDateFrom] = useState(null);
    const [filterDateTo, setFilterDateTo] = useState(null);
    const [selectedFilterTypes, setSelectedFilterTypes] = useState([]);
    // Fetch available document types
    const { data: documentTypesData, isLoading: isLoadingTypes } = useReadDocumentTypes({ 'company-id': selectedCompanyId }, // Add non-null assertion if confident companyId will be present
    { query: { enabled: !!selectedCompanyId } } // Fetch types as soon as companyId is known
    );
    // Memoize the list of all document type IDs
    const allDocumentTypeIds = useMemo(() => {
        // Ensure all IDs are strings and filter out any undefined/null which might result from String() conversion of nullish values
        return (documentTypesData
            ?.map(type => String(type.id))
            .filter(id => id && id !== 'undefined' && id !== 'null') || []);
    }, [documentTypesData]);
    // Format document types for the FilterSelect component
    const documentTypeOptions = useMemo(() => {
        return (documentTypesData
            ?.map(type => ({
            label: type.name ?? '',
            value: String(type.id ?? ''),
        }))
            .filter(option => option.value &&
            option.label &&
            option.value !== 'undefined' &&
            option.value !== 'null')
            .map(option => ({
            label: option.label,
            value: option.value,
        })) || // Cast to string
            []);
    }, [documentTypesData]);
    // API Data Fetching
    const { data: categorizedData, isLoading: isLoadingModalDocs, error: modalDocsError, refetch: refetchModalDocs, } = useReadDocumentsV2({
        'company-id': selectedCompanyId,
        'sort-by': SortBy?.[1],
        'document-relation': DocumentRelation[0],
        // Use filtered types if available, otherwise all types
        'document-types': selectedFilterTypes.length > 0
            ? selectedFilterTypes.map(t => String(t.value)) // Ensure t.value is string
            : allDocumentTypeIds,
        'date-from': filterDateFrom?.toISOString(),
        'date-to': filterDateTo?.toISOString(),
        'get-uri': true,
        'get-thumbnail-uri': false,
    }, {
        query: {
            // Enable only when modal is open, companyId is known, AND all types have been loaded (for the default case)
            enabled: !!selectedCompanyId &&
                isModalOpen &&
                (allDocumentTypeIds.length > 0 || selectedFilterTypes.length > 0), // Adjust condition
        },
    });
    // Store all fetched documents flatly for easier lookup
    const allFetchedDocuments = useMemo(() => {
        return categorizedData?.flatMap(category => category.items || []) || [];
    }, [categorizedData]);
    // Filtered documents for the modal based on search
    const documentsForModal = useMemo(() => {
        if (!searchTerm) {
            return allFetchedDocuments;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();
        return allFetchedDocuments.filter(doc => {
            const titleMatch = doc.title?.toLowerCase().includes(lowerSearchTerm);
            const typeMatch = doc.typeName?.toLowerCase().includes(lowerSearchTerm);
            const eventTitleMatch = doc.eventTitle
                ?.toLowerCase()
                .includes(lowerSearchTerm);
            return titleMatch || typeMatch || eventTitleMatch;
        });
    }, [allFetchedDocuments, searchTerm]);
    // Fetch details for the currently selected document for viewing
    const { data: detailedDocumentData, isLoading: isLoadingDetailedDoc, error: detailedDocError, } = useReadDocument(viewingDocumentId, // Add non-null assertion, enabled flag handles null
    undefined, // Argument 2: Params (explicitly undefined)
    {
        // Argument 3: Options
        query: {
            enabled: !!viewingDocumentId, // Query is enabled only when viewingDocumentId is not null
        },
    });
    // Fetch access token on mount
    useEffect(() => {
        const fetchToken = async () => {
            try {
                const tokens = await getTokens(); // Assuming getTokens is async
                if (tokens && tokens.accessToken) {
                    setRetrievedAccessToken(tokens.accessToken);
                }
            }
            catch (error) {
                console.error('Error fetching access token:', error);
                // Handle token fetching error if necessary
            }
        };
        fetchToken();
    }, []); // Empty dependency array, so it runs once on mount
    // Connect to Chainlit WebSocket
    useEffect(() => {
        console.log('Retrieved Access Token:', retrievedAccessToken);
        console.log('Enrolment ID:', enrolmentId);
        console.log('Selected Company ID:', selectedCompanyId);
        clearMessages();
        if (enrolmentId && selectedCompanyId && retrievedAccessToken) {
            // Ensure all IDs and token are available
            connect({
                userEnv: {
                    'X-Chainlit-User-Id': enrolmentId,
                    company_id: selectedCompanyId,
                    access_token: retrievedAccessToken, // Use the state variable here
                },
            });
        }
        return () => {
            if (enrolmentId && selectedCompanyId && retrievedAccessToken) {
                disconnect();
            }
        };
    }, [enrolmentId, selectedCompanyId, retrievedAccessToken]); // Add connect/disconnect to dependencies
    // Handlers
    const handleSelectDocumentsClick = useCallback(() => {
        setIsModalOpen(true);
    }, []);
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSearchTerm('');
    }, []);
    const handleConfirmSelection = useCallback(() => {
        handleCloseModal();
    }, [handleCloseModal]);
    const handleNewChat = useCallback(() => {
        setIsNewChatModalOpen(true);
    }, []);
    const handleCloseNewChatModal = useCallback(() => {
        setIsNewChatModalOpen(false);
    }, []);
    const handleConfirmNewChat = useCallback(() => {
        clearMessages();
        console.log('New chat created. Messages cleared.');
        // Disconnect and reconnect
        disconnect();
        if (enrolmentId && selectedCompanyId && retrievedAccessToken) {
            connect({
                userEnv: {
                    'X-Chainlit-User-Id': enrolmentId,
                    company_id: selectedCompanyId,
                    access_token: retrievedAccessToken,
                },
            });
        }
        setIsNewChatModalOpen(false);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [
        clearMessages,
        inputRef,
        disconnect,
        connect,
        enrolmentId,
        selectedCompanyId,
        retrievedAccessToken,
    ]);
    const handleCheckboxChange = useCallback((docId, isChecked) => {
        setSelectedDocuments(prev => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(docId);
            }
            else {
                newSet.delete(docId);
            }
            return newSet;
        });
    }, []);
    const handleSelectAllChange = useCallback((isChecked) => {
        if (isChecked) {
            // Ensure all doc.id are converted to string for the Set<string>
            const docIds = documentsForModal
                .map(doc => String(doc.id))
                .filter(id => id && id !== 'undefined' && id !== 'null');
            setSelectedDocuments(new Set(docIds));
        }
        else {
            setSelectedDocuments(new Set());
        }
    }, [documentsForModal]);
    const areAllSelected = useMemo(() => documentsForModal.length > 0 &&
        selectedDocuments.size === documentsForModal.length, [documentsForModal, selectedDocuments]);
    // Chat Handlers
    const handleSendMessage = () => {
        if (currentMessage.trim() === '')
            return;
        const messageToSend = {
            id: uuidv4(),
            name: 'user',
            type: 'user_message',
            output: currentMessage,
            createdAt: new Date().toISOString(),
        };
        sendMessage(messageToSend, []);
        setCurrentMessage('');
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    };
    const sendPresetQuestion = useCallback((question) => {
        if (question.trim() === '')
            return;
        const messageToSend = {
            id: uuidv4(),
            name: 'user',
            type: 'user_message',
            output: question,
            createdAt: new Date().toISOString(),
        };
        sendMessage(messageToSend, []);
        // setCurrentMessage(''); // Do not clear the input for preset questions, user might want to see it.
        // Focus input after sending
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, [sendMessage, inputRef]);
    const handleViewFirstSelected = useCallback(() => {
        if (selectedDocuments.size === 0) {
            console.log('No documents selected in context.');
            setViewingDocumentId(null);
            setDocumentPreviewInitialPage(null);
            return;
        }
        const firstSelectedId = Array.from(selectedDocuments)[0];
        setViewingDocumentId(firstSelectedId);
        setDocumentPreviewInitialPage(2);
        console.log('Attempting to view document with ID:', firstSelectedId, 'at page 2');
    }, [selectedDocuments, setViewingDocumentId, setDocumentPreviewInitialPage]);
    const handleCloseViewer = useCallback(() => {
        setViewingDocumentId(null);
        setDocumentPreviewInitialPage(null);
    }, [setViewingDocumentId, setDocumentPreviewInitialPage]);
    const handleSetInstance = useCallback(() => {
        // No-op
    }, []);
    // Filter Handlers
    const handleFilterTypeChange = useCallback((selectedOptions) => {
        setSelectedFilterTypes(selectedOptions || []);
    }, [setSelectedFilterTypes]);
    const handleFilterDateFromChange = useCallback((date) => {
        setFilterDateFrom(date);
    }, [setFilterDateFrom]);
    const handleFilterDateToChange = useCallback((date) => {
        setFilterDateTo(date);
    }, [setFilterDateTo]);
    const handleResetFilters = useCallback(() => {
        setFilterDateFrom(null);
        setFilterDateTo(null);
        setSelectedFilterTypes([]);
    }, [setFilterDateFrom, setFilterDateTo, setSelectedFilterTypes]);
    const displayMessages = useMemo(() => {
        const collectedMessages = [];
        function findMessagesRecursively(steps) {
            if (!steps)
                return;
            steps.forEach(step => {
                if (step.type === 'user_message' ||
                    step.type === 'assistant_message' ||
                    step.type === 'llm' ||
                    step.type === 'tool') {
                    collectedMessages.push(step);
                }
                if (step.steps && step.steps.length > 0) {
                    findMessagesRecursively(step.steps);
                }
            });
        }
        findMessagesRecursively(chainlitMessages);
        return collectedMessages.sort((a, b) => {
            const aIsStreaming = a.streaming === true;
            const bIsStreaming = b.streaming === true;
            if (aIsStreaming && !bIsStreaming)
                return 1;
            if (!aIsStreaming && bIsStreaming)
                return -1;
            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();
            return dateA - dateB;
        });
    }, [chainlitMessages]);
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [displayMessages]);
    useEffect(() => {
        if (chainlitConnected && inputRef.current) {
            inputRef.current.focus();
        }
    }, [chainlitConnected]);
    const isLoading = useMemo(() => isLoadingTypes ||
        isLoadingModalDocs ||
        isLoadingDetailedDoc ||
        chatLoading, [isLoadingTypes, isLoadingModalDocs, isLoadingDetailedDoc, chatLoading]);
    return {
        t,
        isModalOpen,
        selectedDocuments,
        searchTerm,
        setSearchTerm,
        currentMessage,
        setCurrentMessage,
        documentPreviewInitialPage,
        setDocumentPreviewInitialPage,
        messagesEndRef,
        messagesContainerRef,
        inputRef,
        animation,
        chainlitMessages,
        chatLoading,
        chatError,
        chainlitConnected,
        viewingDocumentId,
        setViewingDocumentId,
        detailedDocumentData,
        isLoadingDetailedDoc,
        detailedDocError,
        filterDateFrom,
        setFilterDateFrom,
        filterDateTo,
        setFilterDateTo,
        selectedFilterTypes,
        setSelectedFilterTypes,
        documentTypeOptions,
        categorizedData,
        isLoadingModalDocs,
        modalDocsError,
        documentsForModal,
        allFetchedDocuments,
        handleSelectDocumentsClick,
        handleCloseModal,
        handleConfirmSelection,
        handleCheckboxChange,
        handleSelectAllChange,
        areAllSelected,
        handleSendMessage,
        sendPresetQuestion,
        handleViewFirstSelected,
        handleCloseViewer,
        handleSetInstance,
        handleFilterTypeChange,
        handleFilterDateFromChange,
        handleFilterDateToChange,
        handleResetFilters,
        displayMessages,
        isLoading,
        getTypeColor,
        enrolmentId,
        selectedCompanyId,
        connect,
        disconnect,
        refetchModalDocs,
        handleNewChat,
        isNewChatModalOpen,
        handleCloseNewChatModal,
        handleConfirmNewChat, // Add handler to confirm new chat
        // The following are intentionally not returned as they are not directly used by the UI component:
        // uuidv4, moment // Utilities used internally
        // ApiDocument // Type alias used internally
    };
};
